/**
 * A prateleira da casa no painel: o `books_preview.dart` com o `shelf_widgets`
 * dentro.
 *
 * É a fatia que fica entre a galeria e a agenda na lista do painel do Flutter
 * (`dashboard_screen.dart:471`), e é também a porta da estante: `/books` e
 * `/books/detail` não são oferecidos em mais lugar nenhum da casa.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **Uma fileira, não uma por pessoa.** Lá o `BooksPreview` parte a prateleira
 *   em duas — "ME" e o outro —, porque lá a casa é sempre de duas pessoas. Aqui
 *   a casa tem quantas pessoas quiser, e N fileiras meio vazias num painel não
 *   é prévia de nada. Fica uma fileira só, da mais recente para a mais antiga,
 *   e quem pôs cada livro é o nome miúdo em cima do título — a mesma atribuição,
 *   num lugar que não cresce com a casa.
 * - **Dez, e não a prateleira toda.** O `ShelfMarquee` de lá desliza sozinho e
 *   por isso pode carregar tudo. Aqui a fileira é de rolar com o dedo, e o resto
 *   está a um toque na setinha.
 * - **Sem gaveta.** O `BookDetailsDrawer` é uma folha que sobe; no site o livro
 *   já tem tela própria, então o toque leva a `/books/detail` com o livro na
 *   bagagem — o mesmo destino, sem uma segunda folha só para isto.
 */

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  COLECAO_NOSSOS_LIVROS,
  livrosDaCasa,
  nossoLivroParaLivro,
  traduzir,
  usarColecao,
  usarSessao,
  type NossoLivro,
} from '@nosso-sempre/shared';
import { armazem } from '../nucleo/api';
import { FichaDaEstante } from './FichaDaEstante';
import { Icone } from './Icone';
import { Secao } from './Secao';
import './previas.css';

/** Quantos cabem antes de a setinha ser o caminho mais curto. */
const QUANTOS = 10;

export function PreviaDosLivros() {
  const ir = useNavigate();
  const { casa } = usarSessao();
  const documentos = usarColecao(COLECAO_NOSSOS_LIVROS);
  const livros = useMemo(() => livrosDaCasa(armazem), [documentos]);

  const legenda =
    livros.length === 0
      ? traduzir('nada na prateleira')
      : livros.length === 1
        ? traduzir('um livro')
        : `${livros.length} livros`;

  function abrir(nosso: NossoLivro) {
    ir('/books/detail', { state: { livro: nossoLivroParaLivro(nosso, casa) } });
  }

  return (
    <Secao
      icone="livro"
      cor="var(--ambar)"
      titulo={traduzir('Nossos livros')}
      legenda={legenda}
      ponta={
        <Link className="secao-seta" to="/books" aria-label={traduzir('Ver a estante inteira')}>
          <Icone nome="avancar" tamanho={18} />
        </Link>
      }
    >
      {livros.length === 0 ? (
        <p className="previa-convite">
          <Icone nome="mais-circulo" tamanho={18} />
          {traduzir('Nenhum livro ainda. Ache a próxima leitura!')}
        </p>
      ) : (
        <ul className="previa-estante">
          {livros.slice(0, QUANTOS).map((l) => (
            <li key={l.id}>
              <FichaDaEstante
                capa={l.capa}
                icone="livro"
                titulo={l.titulo}
                acima={quemPos(l, casa)}
                abaixo={abaixoDoNome(l)}
                aoTocar={() => abrir(l)}
              />
            </li>
          ))}
        </ul>
      )}
    </Secao>
  );
}

/** O `_subtitleFor`: o autor, o ano, ou os dois com o pontinho no meio. */
function abaixoDoNome(l: NossoLivro): string {
  if (!l.autor) return l.ano;
  if (!l.ano) return l.autor;
  return `${l.autor} • ${l.ano}`;
}

/** O nome de quem pôs, que lá era o rótulo da sub-fileira. */
function quemPos(l: NossoLivro, casa: ReturnType<typeof usarSessao>['casa']): string {
  return casa?.pessoas.find((p) => p.slug === l.quemPos)?.nome ?? '';
}
